/**
 * `/app/account/verify-email` — the screen `USR-003` AC-5 asks for, and the one thing missing from
 * a verification flow that was otherwise finished.
 *
 * ## What was actually broken
 *
 * `POST /auth/verify-email/send` and `/auth/verify-email/complete` have worked for months.
 * **Nothing in any SPA called either of them.** `docs/STATUS.md` recorded the consuming pages as
 * arriving "with the member surface", and the member surface shipped without them, so no member
 * on the platform could reach a verified state by any route.
 *
 * That stopped being cosmetic on 2026-08-29. PR #265 closed a live takeover — the PreSignUp
 * trigger linked a Google identity onto whatever native account held the address, verified or not
 * — by refusing to link onto an **unverified** account. The fix is right and stays. Its cost is
 * that a member who registered with a password and later pressed "Continue with Google" now gets a
 * second account rather than a linked one, and that cost is only acceptable while it is
 * temporary: it ends when a member can verify. This screen is where it ends.
 *
 * ## The position it states, and why it is not a warning
 *
 * ADR 028: verification is **non-blocking at registration and gating at first use**. The account
 * exists, works, and finishes its registration; what it does not have is an address that counts as
 * evidence. So the screen leads with what is already true and then says what verifying buys —
 * `MEMBER_VERIFY_EMAIL_COPY` carries all three sentences, and the reasoning for their shape, in
 * `packages/contracts`.
 *
 * ## Landing from a link
 *
 * The screen reads a code out of `?code=` so a mailed link lands on a pre-filled form. **It does
 * not submit on load**, for `confirm-email-page.tsx`'s reason and it is a real one: a mail client
 * or a link scanner that prefetches URLs would spend a single-use credential before the member
 * ever saw the page, and the verification would appear to have happened by itself. The member
 * presses the button.
 *
 * Nothing composes such a link yet — the mail is Cognito's own, carrying a bare code — and
 * `MEMBER_VERIFY_EMAIL_CODE_PARAM` records why that is a pool change rather than a client one.
 * Reading the parameter now means the day the trigger lands, this screen needs no edit.
 *
 * ## Two credentials arrive at this address, and only one of them has a session behind it
 *
 * `ADM-039` AC-3 (ruling 83, 2026-09-13) gave an administrator a *Send a verification email*
 * action on the member record. The mail it sends is deliberately **the ordinary
 * address-verification mail through the member's own verification path and not a second
 * implementation** — which in practice means it is not Cognito's code at all. It carries an
 * `address-proof` **credential link** (ADR 042), minted by the same private `issueVerificationLink`
 * that mints the organizer's, and it lands here as `?token=<rowId>.<secret>`.
 *
 * So this address is reached by two different credentials, and the screen branches on **which
 * parameter is present** rather than on who is signed in:
 *
 *   - `?token=` → {@link MemberVerifyEmailLinkPanel}. Spent by
 *     `POST /api/organizers/onboarding/email-confirmations`, which is public because the token is
 *     the credential. **No session is required or asked for.**
 *   - anything else → {@link MemberVerifyEmailPage}, `USR-003` AC-5's code form, behind
 *     `ProtectedRoute` as it has always been.
 *
 * ## Why the gate moved off the route and onto the branch
 *
 * The code form's two endpoints authenticate on the caller's own access token: the send resolves
 * the mailbox from the session, and the complete records the proof against the session's user id.
 * There is no anonymous form there to build, and that is also what makes the resend
 * enumeration-safe for free (`USR-003` AC-6) — a caller who names no address cannot learn anything
 * about one. **All of that is still true, and none of it is true of the token.**
 *
 * A gate on the *route* would therefore have made the mail useless to the population it exists
 * for: somebody who opened it on a phone that has never signed in, whose address is unproved,
 * which is often *why* an administrator sent it. They would have met the sign-in interstitial and
 * been asked to prove the account before being allowed to prove the mailbox — the exact loop the
 * mail is meant to get them out of. So {@link MemberVerifyEmailRoute} is the route element, the
 * path is exempt in `auth/public-paths.ts`, and the gate sits inside it around the branch that
 * needs one. A signed-out member with no token still meets the interstitial, unchanged.
 */
import { ApiRequestError, Button, Card, CardContent, Field, Input, Skeleton } from '@h2hspark/ui';
import { CircleX } from 'lucide-react';
import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useMemberSession } from '@/auth/auth-provider';
import { ProtectedRoute } from '@/auth/protected-route';
import { ConfirmationCard } from '@/member/confirmation-card';

import {
  useCompleteEmailVerification,
  useConfirmEmailVerificationLink,
  useOwnProfile,
  useSendEmailVerification,
} from './api';
import {
  MEMBER_PROFILE_ROUTES,
  MEMBER_VERIFY_EMAIL_CODE_PARAM,
  MEMBER_VERIFY_EMAIL_COPY,
  MEMBER_VERIFY_EMAIL_TOKEN_PARAM,
} from './constants';

/**
 * The route element for `/app/account/verify-email`, and the only thing mounted on it.
 *
 * React Router cannot route on a query parameter, so the branch is a component. It is here rather
 * than in `App.tsx` so that the two ways in stay in the file that argues about them — and `App.tsx`
 * carries a comment saying that this is where its gate went.
 *
 * **The token is read here and handed down.** `MemberVerifyEmailLinkPanel` takes it as a prop
 * rather than reading the address itself, so there is one place that decides which credential this
 * visit is holding and it cannot disagree with itself.
 */
export function MemberVerifyEmailRoute(): React.ReactElement {
  const [params] = useSearchParams();
  const token = params.get(MEMBER_VERIFY_EMAIL_TOKEN_PARAM);

  if (token !== null) return <MemberVerifyEmailLinkPanel token={token.trim()} />;
  return (
    <ProtectedRoute>
      <MemberVerifyEmailPage />
    </ProtectedRoute>
  );
}

/**
 * The far end of the administrator's *Send a verification email* (`ADM-039` AC-3).
 *
 * ## Nothing is spent on load
 *
 * The same rule `confirm-email-page.tsx` and the code branch above both hold, and the reason is
 * the same: this token is a single-use credential, and a mail client or a corporate link scanner
 * that prefetches URLs would spend it before the member ever saw the page. The verification would
 * then appear to have happened by itself — and worse, a member who later followed the link for
 * real would be told it no longer works. **The member presses the button.**
 *
 * The organizer's `confirm-email-page.tsx` redeems in an effect instead, and that is a genuine
 * disagreement rather than an oversight: `ORG-004`'s link is the last step of a wizard the person
 * just walked through, so arriving *is* the act. Nobody asked for this mail — an administrator
 * sent it — so arriving here is not consent to anything, and the button is where the consent is.
 *
 * ## One refusal, because the endpoint gives one boolean
 *
 * `POST /organizers/onboarding/email-confirmations` answers `{ confirmed }` and nothing else. A
 * token that has expired, one already spent, one a newer link superseded, one whose address has
 * since moved and one that names nobody are a single `false`, deliberately: telling them apart
 * would let somebody probe which accounts exist by guessing tokens. An **already-verified** address
 * is not a distinguishable fourth case either — a live link over one confirms again and answers
 * `true`, a dead link over one answers the same `false` as any other dead link. So the screen says
 * one thing, names the causes, and points at the resend, which is the correct fix for all of them.
 *
 * ## Signed out is the ordinary case here
 *
 * No profile is read: doing so would need a session this visitor may not have, and the token
 * proves the mailbox on its own. The way on is therefore sign-in for a visitor with no session and
 * My Account for one who has — the only thing the session is used for on this branch.
 */
export function MemberVerifyEmailLinkPanel({
  token,
}: {
  readonly token: string;
}): React.ReactElement {
  const confirm = useConfirmEmailVerificationLink();
  const { session } = useMemberSession();

  /**
   * Five outcomes, named rather than nested, because two of them look alike and are not.
   *
   * `refused` is the endpoint's `{ confirmed: false }` — the credential was presented and
   * declined. `failed` is a 500 or a dropped connection: the credential was never seen, so the
   * link may well still be good and telling the member it is spent would send them to ask for a
   * replacement that fails the same way. `describeRefusal` below draws the same line for the code.
   */
  const outcome: 'confirmed' | 'missing' | 'refused' | 'failed' | 'unspent' =
    confirm.data?.confirmed === true
      ? 'confirmed'
      : confirm.error !== null
        ? 'failed'
        : confirm.isSuccess
          ? 'refused'
          : token === ''
            ? 'missing'
            : 'unspent';

  // The only thing the session is read for on this branch: a visitor with none is sent to sign in
  // rather than to My Account, which would bounce them off the interstitial to the same place.
  // TW 44164351. `outline` is the design's *neutral* pill — the marketing header's second action.
  // Inside the member app a secondary control is the brand's own Secondary: a 1.5px purple edge on
  // the card with a purple label, which is what the style guide draws and what *Browse events* on
  // Your Events and *Browse everyone* on the matches screen already are.
  const wayOut = (
    <Button asChild variant="brand-outline">
      {session === null ? (
        <Link to={MEMBER_PROFILE_ROUTES.signIn}>{MEMBER_VERIFY_EMAIL_COPY.linkSignIn}</Link>
      ) : (
        <Link to={MEMBER_PROFILE_ROUTES.account}>{MEMBER_VERIFY_EMAIL_COPY.back}</Link>
      )}
    </Button>
  );

  // TW 44261258: the confirmation is the member app's confirmation card, the reservation's
  // *You're going!* layout (Brad, 2026-10-01, "Go with Option A"). Its one action goes where
  // `wayOut` does: sign in for a visitor with no session, My Account for a member.
  if (outcome === 'confirmed') {
    return (
      <div className="pb-8">
        <ConfirmationCard
          heading={MEMBER_VERIFY_EMAIL_COPY.verifiedHeading}
          lead={MEMBER_VERIFY_EMAIL_COPY.verified}
          action={
            session === null
              ? { to: MEMBER_PROFILE_ROUTES.signIn, label: MEMBER_VERIFY_EMAIL_COPY.linkSignIn }
              : { to: MEMBER_PROFILE_ROUTES.account, label: MEMBER_VERIFY_EMAIL_COPY.back }
          }
          announce
        />
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-8">
      <header>
        <h1 className="text-2xl font-brand font-bold">{MEMBER_VERIFY_EMAIL_COPY.title}</h1>
      </header>

      <Card>
        <CardContent className="space-y-4 pt-5">
          {outcome === 'missing' || outcome === 'refused' ? (
            <>
              <p role="alert" className="flex items-start gap-2 text-sm text-destructive">
                <CircleX aria-hidden className="mt-0.5 h-4.5 w-4.5 flex-none" />
                {outcome === 'missing'
                  ? MEMBER_VERIFY_EMAIL_COPY.linkMissing
                  : MEMBER_VERIFY_EMAIL_COPY.linkRefused}
              </p>
              {wayOut}
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">{MEMBER_VERIFY_EMAIL_COPY.linkIntro}</p>
              <Button
                type="button"
                disabled={confirm.isPending}
                onClick={() => {
                  confirm.reset();
                  confirm.mutate({ token });
                }}
              >
                {confirm.isPending
                  ? MEMBER_VERIFY_EMAIL_COPY.submitting
                  : MEMBER_VERIFY_EMAIL_COPY.submit}
              </Button>
              {outcome === 'failed' ? (
                <p role="alert" className="text-sm text-destructive">
                  {confirm.error?.message}
                </p>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Which of the two refusals came back, as the screen's own vocabulary.
 *
 * `AUTH_FLOW_EXPIRED` (410) and `AUTH_INVALID_CODE` (400) are the only two the route raises for a
 * code it will not take. Anything else — a network failure, a 500 — is neither, and is reported as
 * itself rather than being dressed up as a bad code, because telling somebody their code is wrong
 * when the server never saw it sends them to fetch a new one that will fail the same way.
 */
type Refusal = 'expired' | 'refused' | 'other';

function describeRefusal(error: Error | null): Refusal | null {
  if (error === null) return null;
  if (!(error instanceof ApiRequestError)) return 'other';
  // The envelope, not the status. `httpStatusCode` and `errorCode` are set together by the typed
  // errors in `auth-middleware/src/core/errors.ts`, and the code is the half that is stable — a
  // status can be shared by errors that mean different things.
  let errorCode: unknown;
  try {
    errorCode = (JSON.parse(error.responseBody) as { errorCode?: unknown }).errorCode;
  } catch {
    errorCode = undefined;
  }
  if (errorCode === 'AUTH_FLOW_EXPIRED') return 'expired';
  if (errorCode === 'AUTH_INVALID_CODE') return 'refused';
  return 'other';
}

export function MemberVerifyEmailPage(): React.ReactElement {
  const [params] = useSearchParams();
  const profile = useOwnProfile();
  const send = useSendEmailVerification();
  const complete = useCompleteEmailVerification();

  // Seeded from the link once, then owned by the member. Re-seeding on every render would fight
  // whoever was typing, and clearing it on a resend is deliberate — see the handler below.
  const [code, setCode] = React.useState(() => params.get(MEMBER_VERIFY_EMAIL_CODE_PARAM) ?? '');

  const refusal = describeRefusal(complete.error);

  if (profile.isLoading) return <Skeleton className="h-96 w-full" />;

  const emailAddress = profile.data?.private.emailAddress ?? '';
  // The two measures are one fact here: `private.emailVerified` is `user.emailVerifiedAt`, which
  // `/auth/verify-email/complete` writes in the same handler that sets Cognito's flag. A member
  // who just completed sees it because the mutation invalidates the profile query.
  const verified = profile.data?.private.emailVerified === true;

  // TW 44261258: the end state is the member app's confirmation card (Brad, 2026-10-01, "Go with
  // Option A"). Announced, because a member who has just entered their code watches the form turn
  // into it.
  if (verified) {
    return (
      <div className="pb-8">
        <ConfirmationCard
          heading={MEMBER_VERIFY_EMAIL_COPY.verifiedHeading}
          lead={MEMBER_VERIFY_EMAIL_COPY.verified}
          action={{ to: MEMBER_PROFILE_ROUTES.account, label: MEMBER_VERIFY_EMAIL_COPY.back }}
          announce
        />
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-8">
      <header>
        <h1 className="text-2xl font-brand font-bold">{MEMBER_VERIFY_EMAIL_COPY.title}</h1>
      </header>

      {/* The position (AC-5). Three sentences, before anything is asked. */}
      <Card>
        <CardContent className="space-y-2 pt-5">
          <p className="text-sm text-foreground">{MEMBER_VERIFY_EMAIL_COPY.positionAccountWorks}</p>
          <p className="text-sm text-muted-foreground">
            {MEMBER_VERIFY_EMAIL_COPY.positionWhatItUnlocks}
          </p>
          <p className="text-sm text-muted-foreground">
            {MEMBER_VERIFY_EMAIL_COPY.positionWhereTheCodeIs}
          </p>
          <p className="break-all pt-1 text-sm font-brand font-bold text-foreground">
            {emailAddress}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-4 pt-5">
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              const trimmed = code.trim();
              if (trimmed === '') return;
              complete.mutate({ code: trimmed });
            }}
          >
            <Field label={MEMBER_VERIFY_EMAIL_COPY.codeLabel} htmlFor="member-verify-email-code">
              <Input
                id="member-verify-email-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(event) => setCode(event.target.value)}
              />
            </Field>
            <Button type="submit" disabled={code.trim() === '' || complete.isPending}>
              {complete.isPending
                ? MEMBER_VERIFY_EMAIL_COPY.submitting
                : MEMBER_VERIFY_EMAIL_COPY.submit}
            </Button>
          </form>

          {refusal !== null ? (
            <p role="alert" className="text-sm text-destructive">
              {refusal === 'expired'
                ? MEMBER_VERIFY_EMAIL_COPY.expired
                : refusal === 'refused'
                  ? MEMBER_VERIFY_EMAIL_COPY.refused
                  : complete.error?.message}
            </p>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 pt-5">
          <Button
            type="button"
            variant="brand-outline"
            disabled={send.isPending}
            onClick={() => {
              // **The field is cleared.** A resend replaces the outstanding code, so whatever
              // is in the box is now the one code guaranteed not to work — leaving it there
              // would invite the member to press Verify again and read the refusal as proof
              // that the resend failed too.
              setCode('');
              complete.reset();
              send.mutate();
            }}
          >
            {send.isPending ? MEMBER_VERIFY_EMAIL_COPY.resending : MEMBER_VERIFY_EMAIL_COPY.resend}
          </Button>
          {send.isSuccess ? (
            <p role="status" className="text-sm text-foreground">
              {MEMBER_VERIFY_EMAIL_COPY.resendNotice}
            </p>
          ) : null}
          {send.error !== null ? (
            <p role="alert" className="text-sm text-destructive">
              {send.error.message}
            </p>
          ) : null}
          <div className="pt-1">
            <Link className="auth-ui-link text-sm" to={MEMBER_PROFILE_ROUTES.account}>
              {MEMBER_VERIFY_EMAIL_COPY.back}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
